export interface IExtraConnectionStringParam {
  key: string;
  value: string;
}

export interface IConnectionStringParams {
  [key: string]: string | IExtraConnectionStringParam[] | undefined;
  extraParams?: IExtraConnectionStringParam[];
}

export interface IPostgreSQLConnectionParams {
  [key: string]: string | IExtraConnectionStringParam[] | undefined;
  Server?: string;
  Database?: string;
  "User Id"?: string;
  password?: string;
  extraParams?: IExtraConnectionStringParam[];
}

export interface IMSSQLConnectionParams {
  [key: string]: string | IExtraConnectionStringParam[] | undefined;
  "data source"?: string;
  "initial catalog"?: string;
  "user id"?: string;
  password?: string;
  extraParams?: IExtraConnectionStringParam[];
}

export interface IOracleConnectionParams {
  [key: string]: string | IExtraConnectionStringParam[] | undefined;
  "data source"?: string;
  "user id"?: string;
  password?: string;
  sid?: string;
  extraParams?: IExtraConnectionStringParam[];
}

export interface ISAPConnectionParams {
  [key: string]: string | IExtraConnectionStringParam[] | undefined;
  AppServerHost?: string;
  SystemNumber?: string;
  User?: string;
  Password?: string;
  Client?: string;
  Language?: string;
  extraParams?: IExtraConnectionStringParam[];
}

export class ConnectionStringBuilderService {
  // These are the NORMALIZED keys, so they must match the field paths each
  // create-connection-*.tsx form binds — otherwise antd never renders the value
  // and the next save drops the key from the connection string.
  private static mainConnectionStringProps = {
    PostgreSQL: ["Server", "Database", "User Id", "password"],
    MSSQL: ["data source", "initial catalog", "user id", "password"],
    Oracle: ["data source", "user id", "password", "sid"]
  };

  private static propertyNameMapping: Record<string, Record<string, string>> = {
    PostgreSQL: {
      server: "Server",
      database: "Database",
      userid: "User Id",
      uid: "User Id",
      user: "User Id",
      username: "User Id",
      pwd: "password",
      password: "password"
    },
    MSSQL: {
      server: "data source",
      datasource: "data source",
      host: "data source",
      database: "initial catalog",
      initialcatalog: "initial catalog",
      userid: "user id",
      uid: "user id",
      user: "user id",
      username: "user id",
      pwd: "password",
      password: "password"
    },
    Oracle: {
      server: "data source",
      datasource: "data source",
      host: "data source",
      userid: "user id",
      uid: "user id",
      user: "user id",
      username: "user id",
      pwd: "password",
      password: "password"
    }
  };

  public static buildConnectionString(parsedConnectionString: IConnectionStringParams): string {
    const extraParamsObj: Record<string, string> = {};

    if (parsedConnectionString?.extraParams) {
      parsedConnectionString.extraParams.forEach((param: IExtraConnectionStringParam) => {
        extraParamsObj[param.key] = param.value;
      });
    }

    const connectionParams: Record<string, string> = {};

    Object.entries(parsedConnectionString).forEach(([key, value]) => {
      if (key !== "extraParams" && typeof value === "string") {
        connectionParams[key] = value;
      }
    });

    Object.assign(connectionParams, extraParamsObj);

    let connectionString = "";

    Object.entries(connectionParams).forEach(([key, value], index) => {
      connectionString += `${key}=${value}`;
      if (index < Object.entries(connectionParams).length - 1) {
        connectionString += ";";
      }
    });

    Object.entries(connectionParams).forEach(([key, value]) => {
      connectionString = connectionString.replace(`{${value}}`, value);
    });

    return connectionString;
  }

  public static buildOracleConnectionString(parsedConnectionString: IOracleConnectionParams): string {
    const extraParamsObj: Record<string, string> = {};

    if (parsedConnectionString?.extraParams) {
      parsedConnectionString.extraParams.forEach((param: IExtraConnectionStringParam) => {
        extraParamsObj[param.key] = param.value;
      });
    }

    const connectionParams: Record<string, string> = {};

    Object.entries(parsedConnectionString).forEach(([key, value]) => {
      if (key !== "extraParams" && typeof value === "string") {
        connectionParams[key] = value;
      }
    });

    Object.assign(connectionParams, extraParamsObj);

    if (
      connectionParams["data source"] &&
      parsedConnectionString.sid &&
      typeof parsedConnectionString.sid === "string"
    ) {
      connectionParams["data source"] = `${connectionParams["data source"]}/${parsedConnectionString.sid}`;
      delete connectionParams.sid;
    }

    let connectionString = "";

    Object.entries(connectionParams).forEach(([key, value], index) => {
      connectionString += `${key}=${value}`;
      if (index < Object.entries(connectionParams).length - 1) {
        connectionString += ";";
      }
    });

    Object.entries(connectionParams).forEach(([key, value]) => {
      connectionString = connectionString.replace(`{${value}}`, value);
    });

    return connectionString;
  }

  public static parsePostgreSQLConnectionString(connectionString: string): IPostgreSQLConnectionParams {
    const keyValuePairs: Record<string, string> = {};

    const pairs = connectionString.split(";");

    pairs.forEach((pair) => {
      if (pair.trim()) {
        const equalSignIndex = pair.indexOf("=");
        if (equalSignIndex > 0) {
          const key = pair.substring(0, equalSignIndex).trim();
          const value = pair.substring(equalSignIndex + 1).trim();
          keyValuePairs[key] = value;
        }
      }
    });

    return this.normalizeConnectionStringParams(keyValuePairs, "PostgreSQL") as IPostgreSQLConnectionParams;
  }

  public static parseMSSQLConnectionString(connectionString: string): IMSSQLConnectionParams {
    const obj = ConnectionStringBuilderService.parseConnectionString(connectionString) as Record<string, string>;
    return this.normalizeConnectionStringParams(obj, "MSSQL") as IMSSQLConnectionParams;
  }

  public static parseOracleConnectionString(connectionString: string): IOracleConnectionParams {
    const obj = ConnectionStringBuilderService.parseConnectionString(connectionString) as Record<string, string>;
    const result = this.normalizeConnectionStringParams(obj, "Oracle") as IOracleConnectionParams;

    if (result["data source"]) {
      const splittedDatasource = result["data source"].split("/");
      if (splittedDatasource.length > 1) {
        const datasourcePart = splittedDatasource.slice(0, splittedDatasource.length - 1);
        result["data source"] = datasourcePart.join("/");
        result.sid = splittedDatasource[splittedDatasource.length - 1];
      }
    }

    return result;
  }

  public static buildSAPConnectionString(parsedConnectionString: ISAPConnectionParams): string {
    return this.buildConnectionString(parsedConnectionString);
  }

  public static parseSAPConnectionString(connectionString: string): ISAPConnectionParams {
    const obj = ConnectionStringBuilderService.parseConnectionString(connectionString) as Record<string, string>;
    const result: ISAPConnectionParams = { extraParams: [] };

    const knownKeys = ["appserverhost", "systemnumber", "user", "password", "client", "language"];

    Object.entries(obj).forEach(([key, value]) => {
      const lowerKey = key.toLowerCase();
      switch (lowerKey) {
        case "appserverhost":
          result.AppServerHost = value;
          break;
        case "systemnumber":
          result.SystemNumber = value;
          break;
        case "user":
          result.User = value;
          break;
        case "password":
          result.Password = value;
          break;
        case "client":
          result.Client = value;
          break;
        case "language":
          result.Language = value;
          break;
        default:
          if (!result.extraParams) result.extraParams = [];
          result.extraParams.push({ key, value });
          break;
      }
    });

    return result;
  }

  private static normalizeConnectionStringParams(
    params: Record<string, string>,
    dbType: keyof typeof ConnectionStringBuilderService.propertyNameMapping
  ): IConnectionStringParams {
    const extraParams: IExtraConnectionStringParam[] = [];
    const normalizedParams: Record<string, string | IExtraConnectionStringParam[]> = {};
    const keysToIgnore: string[] = [];

    Object.entries(params).forEach(([key, value]) => {
      const normalizedKey = key.toLowerCase().replace(/\s+/g, "");
      const mapping = this.propertyNameMapping[dbType];

      if (mapping[normalizedKey]) {
        normalizedParams[mapping[normalizedKey]] = value;
        keysToIgnore.push(key);
      } else if (this.mainConnectionStringProps[dbType].includes(key)) {
        normalizedParams[key] = value;
        keysToIgnore.push(key);
      } else {
        extraParams.push({ key, value });
        keysToIgnore.push(key);
      }
    });

    normalizedParams.extraParams = extraParams;

    return normalizedParams as IConnectionStringParams;
  }

  public static parseConnectionString(connectionString: string): Record<string, string> {
    if (!connectionString) return {};

    const keyValuePairs: Record<string, string> = {};

    const pairs = connectionString.split(";");

    pairs.forEach((pair) => {
      if (pair.trim()) {
        const equalSignIndex = pair.indexOf("=");
        if (equalSignIndex > 0) {
          const key = pair.substring(0, equalSignIndex).trim();
          const value = pair.substring(equalSignIndex + 1).trim();
          keyValuePairs[key] = value;
        }
      }
    });

    return keyValuePairs;
  }
}
